// ./src/hooks/useTravelReport.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  TravelReport,
  TravelReportService,
  TravelReportFilter,
} from "@/utils/api";
import { TravelTypeValue } from "@/config/travelTypes";
import { toApiDateRange } from "@/utils";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface UseTravelReportOptions {
  /** Auto-load data on mount with default filters */
  autoLoad?: boolean;

  /** Current user ID - data reloads when this changes */
  userId?: string | null;

  /** Initial car IDs filter (supports multiple vehicles) */
  carIds?: string[];

  /** Initial tag IDs filter */
  tagIds?: string[];

  /** Initial travel types filter */
  travelTypes?: TravelTypeValue[];

  /** Initial date from (local date string YYYY-MM-DD) - defaults to start of current year */
  dateFrom?: string | null;

  /** Initial date to (local date string YYYY-MM-DD) - defaults to today */
  dateTo?: string | null;
}

export interface TravelReportFilters {
  /** Filter by car IDs (empty = all vehicles) */
  carIds: string[];
  /** Filter by tag IDs */
  tagIds: string[];
  /** Filter by travel types: business, personal, medical, charity, commute */
  travelTypes: TravelTypeValue[];
  /** Start date (local YYYY-MM-DD) - required for API */
  dateFrom: string;
  /** End date (local YYYY-MM-DD) - required for API */
  dateTo: string;
}

export interface UseTravelReportReturn {
  // Data state
  /** The report data */
  report: TravelReport | null;

  /** Whether the report is loading */
  loading: boolean;

  /** Whether the report has no data */
  isEmpty: boolean;

  /** Whether the report has no trips (but may have other data) */
  hasNoTrips: boolean;

  // Current filters (local dates)
  /** Current filter state */
  filters: TravelReportFilters;

  // Core operations
  /** Fetch report with filters (dates should be local YYYY-MM-DD strings) */
  fetchReport: (
    filter?: Partial<TravelReportFilters>
  ) => Promise<TravelReport | null>;

  /** Refresh report with current filters */
  refresh: () => Promise<void>;

  /** Reset to initial state */
  reset: () => void;

  // Filter setters
  /** Set car IDs filter */
  setCarIds: (carIds: string[]) => void;

  /** Set tag IDs filter */
  setTagIds: (tagIds: string[]) => void;

  /** Set travel types filter */
  setTravelTypes: (travelTypes: TravelTypeValue[]) => void;

  /** Set date range filter (local dates) */
  setDateRange: (dateFrom: string, dateTo: string) => void;

  /** Set all filters at once */
  setFilters: (filters: Partial<TravelReportFilters>) => void;

  // Error handling
  /** Last error message */
  lastError: string | null;

  /** Clear the error */
  clearError: () => void;

  // Helpers
  /** Get default date range (current tax year: Jan 1 to today) */
  getDefaultDateRange: () => { dateFrom: string; dateTo: string };

  /** Get previous tax year date range */
  getPreviousYearDateRange: () => { dateFrom: string; dateTo: string };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get default date range for travel report (current tax year: Jan 1 to today)
 * Travel reports are typically for tax purposes, so default to tax year
 */
const getDefaultDateRangeStatic = (): { dateFrom: string; dateTo: string } => {
  const today = dayjs().startOf("day");
  const startOfYear = today.startOf("year");

  return {
    dateFrom: startOfYear.format("YYYY-MM-DD"),
    dateTo: today.format("YYYY-MM-DD"),
  };
};

/**
 * Get previous tax year date range (useful for filing taxes)
 */
const getPreviousYearDateRangeStatic = (): {
  dateFrom: string;
  dateTo: string;
} => {
  const today = dayjs().startOf("day");
  const previousYear = today.subtract(1, "year");
  const startOfPreviousYear = previousYear.startOf("year");
  const endOfPreviousYear = previousYear.endOf("year");

  return {
    dateFrom: startOfPreviousYear.format("YYYY-MM-DD"),
    dateTo: endOfPreviousYear.format("YYYY-MM-DD"),
  };
};

// ============================================
// Hook
// ============================================

export const useTravelReport = (
  options: UseTravelReportOptions = {}
): UseTravelReportReturn => {
  const {
    autoLoad = false,
    userId = null,
    carIds: initialCarIds = [],
    tagIds: initialTagIds = [],
    travelTypes: initialTravelTypes = [],
    dateFrom: initialDateFrom = null,
    dateTo: initialDateTo = null,
  } = options;

  // Track previous user ID for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Get default dates if not provided
  const defaultDates = useMemo(() => getDefaultDateRangeStatic(), []);

  // Data state
  const [report, setReport] = useState<TravelReport | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter state (local dates) - dateFrom/dateTo are required, so always have defaults
  const [filters, setFiltersState] = useState<TravelReportFilters>({
    carIds: initialCarIds,
    tagIds: initialTagIds,
    travelTypes: initialTravelTypes,
    dateFrom: initialDateFrom ?? defaultDates.dateFrom,
    dateTo: initialDateTo ?? defaultDates.dateTo,
  });

  // Use ref to track current filters for stable callbacks
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Error state
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(() => report === null && !loading, [report, loading]);

  const hasNoTrips = useMemo(
    () => report !== null && (!report.trips || report.trips.length === 0),
    [report]
  );

  // ============================================
  // Core Operations
  // ============================================

  /**
   * Fetch report with filters
   * Dates should be provided as local YYYY-MM-DD strings
   */
  const fetchReport = useCallback(
    async (
      filter?: Partial<TravelReportFilters>
    ): Promise<TravelReport | null> => {
      setLoading(true);
      setLastError(null);

      // Use ref to get current filters (avoids dependency on filters state)
      const currentFilters = filtersRef.current;

      // Merge with current filters
      const mergedFilters: TravelReportFilters = {
        carIds:
          filter?.carIds !== undefined ? filter.carIds : currentFilters.carIds,
        tagIds:
          filter?.tagIds !== undefined ? filter.tagIds : currentFilters.tagIds,
        travelTypes:
          filter?.travelTypes !== undefined
            ? filter.travelTypes
            : currentFilters.travelTypes,
        dateFrom:
          filter?.dateFrom !== undefined
            ? filter.dateFrom
            : currentFilters.dateFrom,
        dateTo:
          filter?.dateTo !== undefined ? filter.dateTo : currentFilters.dateTo,
      };

      // Update filter state only if changed
      const filtersChanged =
        JSON.stringify(mergedFilters.carIds) !==
        JSON.stringify(currentFilters.carIds) ||
        JSON.stringify(mergedFilters.tagIds) !==
        JSON.stringify(currentFilters.tagIds) ||
        JSON.stringify(mergedFilters.travelTypes) !==
        JSON.stringify(currentFilters.travelTypes) ||
        mergedFilters.dateFrom !== currentFilters.dateFrom ||
        mergedFilters.dateTo !== currentFilters.dateTo;

      if (filtersChanged) {
        setFiltersState(mergedFilters);
      }

      try {
        // Convert local dates to UTC for API
        const { dateFrom, dateTo } = toApiDateRange(
          mergedFilters.dateFrom,
          mergedFilters.dateTo
        );

        // Build API filter - dateFrom and dateTo are required
        const apiFilter: TravelReportFilter = {
          dateFrom: dateFrom || mergedFilters.dateFrom,
          dateTo: dateTo || mergedFilters.dateTo,
        };

        if (mergedFilters.carIds.length > 0) {
          apiFilter.carId = mergedFilters.carIds;
        }

        if (mergedFilters.tagIds.length > 0) {
          apiFilter.tagId = mergedFilters.tagIds;
        }

        if (mergedFilters.travelTypes.length > 0) {
          apiFilter.travelType = mergedFilters.travelTypes;
        }

        const result = await TravelReportService.get(apiFilter);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const reportData = Array.isArray(result.data)
            ? (result.data[0] as TravelReport)
            : (result.data as TravelReport);

          setReport(reportData || null);
          return reportData || null;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load travel report";
          setLastError(errorMessage);
          setReport(null);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load travel report";
        setLastError(errorMessage);
        setReport(null);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [] // No dependencies - uses refs
  );

  /**
   * Refresh report with current filters
   */
  const refresh = useCallback(async () => {
    await fetchReport();
  }, [fetchReport]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    const defaultRange = getDefaultDateRangeStatic();
    setReport(null);
    setFiltersState({
      carIds: initialCarIds,
      tagIds: initialTagIds,
      travelTypes: initialTravelTypes,
      dateFrom: initialDateFrom ?? defaultRange.dateFrom,
      dateTo: initialDateTo ?? defaultRange.dateTo,
    });
    setLastError(null);
    setLoading(false);
  }, [
    initialCarIds,
    initialTagIds,
    initialTravelTypes,
    initialDateFrom,
    initialDateTo,
  ]);

  // ============================================
  // Filter Setters
  // ============================================

  const setCarIds = useCallback((carIds: string[]) => {
    setFiltersState((prev) => ({ ...prev, carIds }));
  }, []);

  const setTagIds = useCallback((tagIds: string[]) => {
    setFiltersState((prev) => ({ ...prev, tagIds }));
  }, []);

  const setTravelTypes = useCallback((travelTypes: TravelTypeValue[]) => {
    setFiltersState((prev) => ({ ...prev, travelTypes }));
  }, []);

  const setDateRange = useCallback((dateFrom: string, dateTo: string) => {
    setFiltersState((prev) => ({ ...prev, dateFrom, dateTo }));
  }, []);

  const setFilters = useCallback((newFilters: Partial<TravelReportFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // ============================================
  // Error Handling
  // ============================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // ============================================
  // Stable helper functions
  // ============================================

  const getDefaultDateRange = useCallback(() => {
    return getDefaultDateRangeStatic();
  }, []);

  const getPreviousYearDateRange = useCallback(() => {
    return getPreviousYearDateRangeStatic();
  }, []);

  // ============================================
  // Auto-load and User Change Effects
  // ============================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        fetchReport();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        fetchReport();
      }
    }
  }, [userId, autoLoad, fetchReport, reset]);

  // ============================================
  // Return
  // ============================================

  return {
    // Data state
    report,
    loading,
    isEmpty,
    hasNoTrips,

    // Current filters
    filters,

    // Core operations
    fetchReport,
    refresh,
    reset,

    // Filter setters
    setCarIds,
    setTagIds,
    setTravelTypes,
    setDateRange,
    setFilters,

    // Error handling
    lastError,
    clearError,

    // Helpers
    getDefaultDateRange,
    getPreviousYearDateRange,
  };
};